import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedNumberInput } from '@rd-web-markets/shared/dist/util';
import claimSoftwareConsumablesService from '../../services/claim_software_consumables.service';
import claimEmployeeRndApportionmentsService from '../../services/claim/claim_employee_rnd_apportionments.service';
import { useErrorHandling } from '@rd-web-markets/shared/dist/hooks/useErrorHandling';
import ApportionmentsList from '../ApportionmentsList';
import ChangeHighlightedImmutableUnderReviewControl from '@rd-web-markets/shared/dist/util/reviewEditControls/ChangeHighlightedImmutableUnderReviewControl';
import { DeleteButton } from '@rd-web-markets/shared/dist/util/buttons';
export function ITConsumableRow(_ref) {
  let {
    sortedClaimProjectReports,
    index,
    claim,
    handleDelete,
    claimSoftwareConsumables,
    setClaimSoftwareConsumables,
    refreshClaim,
    handleError,
    headerScrollPosition,
    isShowInvolvement
  } = _ref;
  const [hasError, setHasError] = useState(false);
  const user = useSelector(state => state.auth.user);
  const claimEmployeeRndApportRef = useRef(null);
  useEffect(() => {
    if (claimEmployeeRndApportRef.current) {
      claimEmployeeRndApportRef.current.scrollTo({
        left: headerScrollPosition
      });
    }
  }, [claimEmployeeRndApportRef, headerScrollPosition]);
  const claimSoftwareConsumable = useMemo(() => {
    return {
      ...claimSoftwareConsumables[index]
    };
  }, [claimSoftwareConsumables, index]);
  const dispatch = useDispatch();
  const updateSoftwareConsumables = useCallback(softwareSoftwareConsumable => {
    const newSoftwareConsumables = [...claimSoftwareConsumables];
    newSoftwareConsumables[index] = {
      ...softwareSoftwareConsumable
    };
    setClaimSoftwareConsumables([...newSoftwareConsumables]);
  }, [claimSoftwareConsumables, index, setClaimSoftwareConsumables]);
  const handleApportionmentChange = useErrorHandling(useCallback(async (softwareSoftwareConsumable, apportionment) => {
    const oldApportionment = softwareSoftwareConsumable.claim_employee_rnd_apportionments.find(a => a.claim_project_report_id === apportionment.claim_project_report_id);
    const index = softwareSoftwareConsumable.claim_employee_rnd_apportionments.indexOf(oldApportionment);
    const newApportionments = [...softwareSoftwareConsumable.claim_employee_rnd_apportionments];
    if (index === -1) {
      newApportionments.push(apportionment);
    } else {
      newApportionments[index] = {
        ...apportionment
      };
    }
    const updatedSoftwareConsumable = await claimSoftwareConsumablesService.get(softwareSoftwareConsumable);
    updatedSoftwareConsumable.claim_employee_rnd_apportionments = newApportionments;
    updateSoftwareConsumables(updatedSoftwareConsumable);
  }, [updateSoftwareConsumables]));
  const handleChange = useCallback(event => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    claimSoftwareConsumable[event.target.name] = value;
    updateSoftwareConsumables(claimSoftwareConsumable);
  }, [updateSoftwareConsumables, claimSoftwareConsumable]);
  const saveChange = useCallback(() => {
    let method;
    if (claimSoftwareConsumable.id) {
      method = 'update';
    } else {
      method = 'create';
    }
    claimSoftwareConsumablesService[method](claimSoftwareConsumable).then(updatedSoftwareConsumable => {
      setHasError(false);
      updateSoftwareConsumables(updatedSoftwareConsumable);
      refreshClaim();
    }).catch(e => {
      setHasError(true);
      dispatch(handleError(e));
    });
  }, [updateSoftwareConsumables, claimSoftwareConsumable, dispatch, refreshClaim]);
  const hasPermissions = () => {
    return user.account_type !== 'customer' && user.account_type !== 'accountant';
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("tr", {
    className: hasError ? 'bg-warning' : ''
  }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: Form.Control,
    onChange: handleChange,
    onBlur: saveChange,
    disabled: claim.financials_locked || !hasPermissions(),
    name: "name",
    object: {
      ...claimSoftwareConsumable,
      claim: {
        ...claim,
        ...claimSoftwareConsumable.claim
      }
    },
    size: "sm",
    type: "text"
  })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: FormattedNumberInput,
    onChange: handleChange,
    onBlur: saveChange,
    disabled: claim.financials_locked || !hasPermissions(),
    name: "net_amount",
    object: {
      ...claimSoftwareConsumable,
      claim: {
        ...claim,
        ...claimSoftwareConsumable.claim
      }
    },
    size: "sm",
    type: "text"
  })), isShowInvolvement && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: Form.Control,
    onChange: handleChange,
    onBlur: saveChange,
    object: {
      ...claimSoftwareConsumable,
      claim: {
        ...claim,
        ...claimSoftwareConsumable.claim
      }
    },
    disabled: claim.financials_locked || user.account_type === 'customer',
    name: "direct_rnd_apportionment",
    type: "text",
    size: "sm"
  })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: Form.Control,
    onChange: handleChange,
    onBlur: saveChange,
    object: {
      ...claimSoftwareConsumable,
      claim: {
        ...claim,
        ...claimSoftwareConsumable.claim
      }
    },
    disabled: claim.financials_locked || user.account_type === 'customer',
    name: "indirect_rnd_apportionment",
    type: "text",
    size: "sm"
  })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: FormattedNumberInput,
    decimalScale: 1,
    onChange: handleChange,
    onBlur: saveChange,
    disabled: claim.financials_locked || !hasPermissions() || claim.claim_group.project_methodology !== 'employee_basis',
    name: "rnd_apportionment",
    object: {
      ...claimSoftwareConsumable,
      claim: {
        ...claim,
        ...claimSoftwareConsumable.claim
      }
    },
    size: "sm",
    type: "text"
  }))), claim.claim_group.project_methodology === 'project_basis' && /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ApportionmentsList, {
    claim: claim,
    sortedClaimProjectReports: sortedClaimProjectReports,
    headerScrollPosition: headerScrollPosition,
    claimEmployeeRndApportRef: claimEmployeeRndApportRef,
    handleApportionmentChange: handleApportionmentChange,
    claimEmployeeRndApportionmentsService: claimEmployeeRndApportionmentsService,
    model: claimSoftwareConsumable,
    apportionmentable_type: "UkSection::ClaimSoftwareConsumable",
    isShowInvolvement: isShowInvolvement
  })), !isShowInvolvement && /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: FormattedNumberInput,
    decimalScale: 1,
    onChange: handleChange,
    onBlur: saveChange,
    disabled: claim.financials_locked || !hasPermissions() || claim.claim_group.project_methodology !== 'employee_basis',
    name: "rnd_apportionment",
    object: {
      ...claimSoftwareConsumable,
      claim: {
        ...claim,
        ...claimSoftwareConsumable.claim
      }
    },
    size: "sm",
    type: "text"
  })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: FormattedNumberInput,
    disabled: true,
    name: "rnd_amount",
    object: {
      ...claimSoftwareConsumable,
      claim: {
        ...claim,
        ...claimSoftwareConsumable.claim
      }
    },
    size: "sm",
    type: "text"
  })), hasPermissions() && /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(DeleteButton, {
    style: {
      height: '28px'
    },
    iconClassName: "text-danger fa-2x",
    disabled: claim.is_locked || claim.financials_locked || claim.active_change_set,
    isImmutableUnderReview: true,
    onClick: handleDelete
  }))));
}